import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

interface QAFeedback {
  id: string
  task_id: string
  issue_type: string
  issue_description: string
  issue_category: string | null
  severity: 'critical' | 'major' | 'minor'
  code_before: string | null
  code_after: string | null
  file_path: string | null
  occurrence_count: number
  created_at: string
  status: 'pending' | 'approved' | 'rejected' | 'duplicate'
}

interface CodeFix {
  id: string
  issue_pattern: string
  issue_category: string
  severity: string
  fix_template: string
  times_applied: number
  effectiveness_score: number
  created_at: string
}

export function FixReviewPage() {
  const [pendingFeedback, setPendingFeedback] = useState<QAFeedback[]>([])
  const [approvedFixes, setApprovedFixes] = useState<CodeFix[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedFeedback, setSelectedFeedback] = useState<QAFeedback | null>(null)
  const [fixTemplate, setFixTemplate] = useState('')
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)

    const [feedbackRes, fixesRes] = await Promise.all([
      supabase
        .from('qa_feedback')
        .select('*')
        .eq('status', 'pending')
        .order('occurrence_count', { ascending: false })
        .order('created_at', { ascending: true }),
      supabase
        .from('code_fixes')
        .select('*')
        .order('times_applied', { ascending: false })
        .limit(20)
    ])

    if (feedbackRes.data) setPendingFeedback(feedbackRes.data)
    if (fixesRes.data) setApprovedFixes(fixesRes.data)
    setLoading(false)
  }

  async function approveFix(feedback: QAFeedback) {
    if (!fixTemplate.trim()) {
      alert('Please enter a fix template')
      return
    }

    setProcessing(true)

    try {
      // Generate embedding via edge function or skip for now
      // For now, insert without embedding - can backfill later
      const { data: fix, error: fixError } = await supabase
        .from('code_fixes')
        .insert({
          issue_pattern: feedback.issue_type,
          issue_category: feedback.issue_category || 'general',
          severity: feedback.severity,
          fix_pattern: feedback.issue_type,
          fix_template: fixTemplate,
          example_before: feedback.code_before,
          example_after: feedback.code_after,
          auto_apply: false
        })
        .select()
        .single()

      if (fixError) throw fixError

      // Update feedback as approved
      const { error: updateError } = await supabase
        .from('qa_feedback')
        .update({
          status: 'approved',
          promoted_fix_id: fix?.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', feedback.id)

      if (updateError) throw updateError

      setSelectedFeedback(null)
      setFixTemplate('')
      loadData()
    } catch (err) {
      console.error('Failed to approve fix:', err)
      alert('Failed to approve fix')
    } finally {
      setProcessing(false)
    }
  }

  async function rejectFix(feedback: QAFeedback, reason: string) {
    setProcessing(true)

    try {
      await supabase
        .from('qa_feedback')
        .update({
          status: 'rejected',
          rejection_reason: reason,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', feedback.id)

      setSelectedFeedback(null)
      loadData()
    } catch (err) {
      console.error('Failed to reject:', err)
    } finally {
      setProcessing(false)
    }
  }

  async function markDuplicate(feedback: QAFeedback) {
    setProcessing(true)

    try {
      // Increment occurrence count of the original
      await supabase
        .from('qa_feedback')
        .update({
          status: 'duplicate',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', feedback.id)

      setSelectedFeedback(null)
      loadData()
    } catch (err) {
      console.error('Failed to mark duplicate:', err)
    } finally {
      setProcessing(false)
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20'
      case 'major': return 'text-yellow-400 bg-yellow-500/20'
      case 'minor': return 'text-blue-400 bg-blue-500/20'
      default: return 'text-slate-400 bg-slate-500/20'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-8 flex items-center justify-center">
        <div className="text-cyan-400">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-2 text-cyan-400">Fix Review</h1>
        <p className="text-slate-400 mb-8">Review QA failures and promote to permanent fixes</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pending Reviews */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse" />
              Pending Review ({pendingFeedback.length})
            </h2>

            {pendingFeedback.length === 0 ? (
              <div className="bg-slate-800 rounded-lg p-8 text-center text-slate-500">
                No pending fixes to review
              </div>
            ) : (
              <div className="space-y-3">
                {pendingFeedback.map(feedback => (
                  <div
                    key={feedback.id}
                    onClick={() => {
                      setSelectedFeedback(feedback)
                      setFixTemplate(feedback.issue_description)
                    }}
                    className={`bg-slate-800 rounded-lg p-4 cursor-pointer transition-all hover:bg-slate-700 border-2 ${
                      selectedFeedback?.id === feedback.id
                        ? 'border-cyan-400'
                        : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(feedback.severity)}`}>
                            {feedback.severity}
                          </span>
                          {feedback.issue_category && (
                            <span className="px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300">
                              {feedback.issue_category}
                            </span>
                          )}
                          {feedback.occurrence_count > 1 && (
                            <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                              ×{feedback.occurrence_count}
                            </span>
                          )}
                        </div>
                        <h3 className="font-medium text-white truncate">{feedback.issue_type}</h3>
                        <p className="text-sm text-slate-400 line-clamp-2">{feedback.issue_description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Feedback Details / Active Fixes */}
          <div>
            {selectedFeedback ? (
              <div className="bg-slate-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Review: {selectedFeedback.issue_type}</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Description</label>
                    <p className="text-white">{selectedFeedback.issue_description}</p>
                  </div>

                  {selectedFeedback.code_before && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Bad Code Example</label>
                      <pre className="bg-slate-900 rounded p-3 text-sm text-red-300 overflow-x-auto">
                        {selectedFeedback.code_before}
                      </pre>
                    </div>
                  )}

                  {selectedFeedback.code_after && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Fixed Code Example</label>
                      <pre className="bg-slate-900 rounded p-3 text-sm text-green-300 overflow-x-auto">
                        {selectedFeedback.code_after}
                      </pre>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Fix Template (instructions for workers)</label>
                    <textarea
                      value={fixTemplate}
                      onChange={e => setFixTemplate(e.target.value)}
                      className="w-full bg-slate-900 rounded p-3 text-white border border-slate-700 focus:border-cyan-400 focus:outline-none"
                      rows={4}
                      placeholder="Describe how to fix this issue..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => approveFix(selectedFeedback)}
                      disabled={processing}
                      className="flex-1 bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded font-medium transition-colors disabled:opacity-50"
                    >
                      {processing ? '...' : '✓ Approve & Add to Fixes'}
                    </button>
                    <button
                      onClick={() => rejectFix(selectedFeedback, 'Not useful')}
                      disabled={processing}
                      className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded font-medium transition-colors disabled:opacity-50"
                    >
                      ✗ Reject
                    </button>
                    <button
                      onClick={() => markDuplicate(selectedFeedback)}
                      disabled={processing}
                      className="bg-slate-600 hover:bg-slate-500 text-white px-4 py-2 rounded font-medium transition-colors disabled:opacity-50"
                    >
                      Duplicate
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-400 rounded-full" />
                  Active Fixes ({approvedFixes.length})
                </h2>

                <div className="space-y-2">
                  {approvedFixes.map(fix => (
                    <div
                      key={fix.id}
                      className="bg-slate-800 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-white">{fix.issue_pattern}</h3>
                          <p className="text-sm text-slate-400">{fix.fix_template.slice(0, 100)}...</p>
                        </div>
                        <div className="text-right">
                          {fix.times_applied > 0 ? (
                            <>
                              <div className="text-cyan-400 font-medium">{fix.times_applied}×</div>
                              <div className="text-xs text-slate-500">applied</div>
                            </>
                          ) : (
                            <div className="text-xs text-slate-500">Not yet applied</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FixReviewPage
